<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <v-layout class="px-4 align-center justify-space-between w-100">
        <div class="d-flex align-center">
          <v-avatar size="36" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2" small
              >mdi-truck-delivery-outline</v-icon
            >
          </v-avatar>
          <div class="font-weight-700 color-custom-blue">Select Supplier</div>
        </div>
        <div>
          <v-btn
            icon
            small
            class="custom-grey-border bg-slate-50"
            :disabled="loading"
            v-on:click="$emit('close', true)"
          >
            <v-icon color="slate-600" small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-layout>
    </template>
    <template v-slot:body>
      <div class="px-4 pb-4 w-100">
        <!-- Search Toolbar Card -->
        <v-card
          flat
          outlined
          class="rounded-lg bg-slate-50 px-3 py-3 mb-3 border-slate"
        >
          <label
            for="supplier-search"
            class="font-size-13 font-weight-600 mb-1 d-block text-slate-700"
          >
            Which supplier would you like to select this for?
          </label>
          <div class="d-flex align-center">
            <v-text-field
              id="supplier-search"
              v-model="search"
              placeholder="Search Suppliers..."
              dense
              filled
              solo
              flat
              hide-details
              color="cyan"
              prepend-inner-icon="mdi-magnify"
              clearable
              :loading="loading"
              :disabled="loading"
              class="custom-erp-input bg-white rounded-lg flex-grow-1"
              v-on:keyup.enter="getSuppliers()"
            />
            <v-btn
              v-on:click="getSuppliers()"
              :loading="loading"
              :disabled="loading"
              height="36"
              depressed
              color="cyan darken-2 white--text"
              class="custom-bold-button ml-2 px-4"
            >
              <v-icon small left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </div>
        </v-card>

        <!-- Supplier List Scrollable Area -->
        <div
          class="pr-1"
          style="max-height: calc(100vh - 320px); overflow-y: auto"
        >
          <div v-if="list && list.length">
            <v-card
              v-for="(row, index) in list"
              :key="`supplier-list-key-${index}`"
              flat
              outlined
              class="rounded-lg px-3 py-2 mb-2 bg-white hover-shadow cursor-pointer transition-fast border-slate"
              v-on:click="selectSupplier(row)"
            >
              <v-row dense align="center">
                <!-- Left: Supplier Info -->
                <v-col cols="12" md="8" class="py-1">
                  <div class="d-flex align-center flex-wrap gap-2 mb-1">
                    <Barcode :barcode="row.barcode" />
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600"
                      >mdi-truck-delivery-outline</v-icon
                    >
                    <template v-if="row.company_name">
                      {{ row.company_name }}
                    </template>
                    <em v-else class="text-muted font-italic font-weight-400"
                      >no supplier name</em
                    >
                  </div>

                  <div
                    class="d-flex align-center flex-wrap font-size-12 text-slate-700 gap-3"
                  >
                    <span
                      v-if="row?.default_person?.display_name"
                      class="d-flex align-center mr-3"
                    >
                      <v-icon small left color="slate-500"
                        >mdi-account-circle-outline</v-icon
                      >
                      <span class="font-weight-600 text-muted mr-1"
                        >Contact:</span
                      >
                      <span class="font-weight-500 text-slate-900">{{
                        row?.default_person?.display_name
                      }}</span>
                    </span>

                    <span
                      v-if="row?.single_billing?.street_1"
                      class="d-flex align-center font-weight-500 custom-nowrap-ellipsis"
                    >
                      <v-icon small left color="slate-500"
                        >mdi-map-marker-outline</v-icon
                      >
                      {{ row?.single_billing?.street_1 }}
                      {{ row?.single_billing?.unit_no }}
                      {{ row?.single_billing?.street_2 }}
                      {{ row?.single_billing?.zip_code }}
                    </span>
                  </div>
                </v-col>

                <!-- Right: Select CTA -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex flex-column align-md-end justify-center py-1"
                >
                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select Supplier
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large
                >mdi-truck-delivery-outline</v-icon
              >
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No Suppliers Found
            </div>
            <div class="caption-1 text-muted max-w-300">
              There are no matching suppliers in the database.
            </div>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { QUERY } from "@/core/services/store/request.module";
import { SET_SUPPLIER } from "@/core/services/store/customer.module";
import ObjectPath from "object-path";

export default {
  name: "select-supplier",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      search: null,
      list: [],
    };
  },
  watch: {
    dialog(param) {
      this.search = null;
      if (param) {
        this.getSuppliers();
      }
    },
  },
  components: {
    Dialog,
    Barcode,
  },
  methods: {
    selectSupplier(row) {
      if (this.loading) {
        return false;
      }
      this.loading = true;
      this.$store
        .dispatch(SET_SUPPLIER, row)
        .then(() => {
          this.$emit("close", true);
          this.search = null;
          this.list = [];
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSuppliers() {
      this.loading = true;
      this.list = [];
      this.$store
        .dispatch(QUERY, {
          url: "supplier-listing",
          data: { search: this.search },
        })
        .then((response) => {
          this.list = ObjectPath.get(response, "data", []);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 50);
    },
  },
};
</script>
